/**
 * Monri verification gate — feature flag koji odlucuje da li korisnik vidi
 * Monri karticno placanje (Step 4) ili stari IPS bankovni-transfer tok
 * (Step 4/5/6 -ips).
 *
 * Pravila (videti `.env.example` za pun opis):
 *   - REACT_APP_USE_MONRI_AVAILABLE = "1" → uvek vrati true (open mode);
 *   - REACT_APP_USE_MONRI_AVAILABLE = "0" → restricted mode:
 *       - logovan user: vrati true samo ako username === REACT_APP_MONRI_USERNAME;
 *       - guest: vrati true samo ako (firstName === REACT_APP_MONRI_FIRSTNAME
 *                                 AND  lastName  === REACT_APP_MONRI_LASTNAME).
 *
 * Sva poredjenja su case-insensitive + trim (npr. "  ELENA  " == "elena").
 *
 * VAZNO: BE mora imati identicnu vrednost u application.properties (kljucevi
 * `use.monri.card.payment.process.available`, `user.monri.card.username`,
 * `user.monri.card.firstname`, `user.monri.card.lastname`). Mismatch znaci da
 * ce FE prikazati Monri ekran a BE odbiti API poziv (ili obrnuto).
 */

const norm = (v: string | null | undefined): string =>
    (v ?? '').trim().toLowerCase();

const isAvailableToAll = (): boolean =>
    process.env.REACT_APP_USE_MONRI_AVAILABLE === '1';

/**
 * Globalni feature flag za karticno placanje — vraca `true` kada je Monri
 * ukljucen za sve korisnike (open mode), `false` u restricted modu (banka jos
 * nije odobrila karticno placanje pa svi idu na IPS bankovni-transfer tok).
 *
 * Razlika u odnosu na {@link shouldUseMonri}: ova funkcija ne uzima u obzir
 * konkretnog korisnika (logged user / guest), nego samo globalno stanje.
 * Koristi se tamo gde nemamo user kontekst — npr. u footeru za odluku da li
 * prikazati logoe platnih kartica (VISA, MC, Maestro, DinaCard, AmEx) ili ih
 * sakriti dok karticno placanje nije pravo dostupno javnosti.
 */
export const isMonriCardPaymentEnabled = (): boolean => isAvailableToAll();

export interface MonriEligibilityInput {
    /** Keycloak `preferred_username` (samo za logovane korisnike). */
    loggedInUsername?: string | null;
    /** Ime iz guest forme (samo za guest korisnike). */
    guestFirstName?: string | null;
    /** Prezime iz guest forme (samo za guest korisnike). */
    guestLastName?: string | null;
}

/**
 * Vraca `true` ako korisnik treba da ide na Monri karticno placanje, `false`
 * ako treba da ide na IPS bankovni-transfer tok.
 */
export const shouldUseMonri = (input: MonriEligibilityInput): boolean => {
    if (isAvailableToAll()) {
        return true;
    }

    const username = norm(input.loggedInUsername);
    if (username) {
        const testUsername = norm(process.env.REACT_APP_MONRI_USERNAME);
        return Boolean(testUsername) && username === testUsername;
    }

    const firstName = norm(input.guestFirstName);
    const lastName = norm(input.guestLastName);
    const testFirstName = norm(process.env.REACT_APP_MONRI_FIRSTNAME);
    const testLastName = norm(process.env.REACT_APP_MONRI_LASTNAME);

    return (
        Boolean(testFirstName) &&
        Boolean(testLastName) &&
        firstName === testFirstName &&
        lastName === testLastName
    );
};
